import { FC, useMemo } from 'react'
import { createRenderTokensToJsx, parseMarkdown } from 'helpers/markdownHelpers'
import { MarkdownToken } from 'marked-ast'
import styles from 'components/features/socialFeed/SocialInsight/SocialInsight.styl'

const cleanUpTokens = (tokens: MarkdownToken[], textLimit: { remain: number } | null = null): MarkdownToken[] => {
  const result = tokens.reduce<MarkdownToken[]>((acc, token) => {
    if (textLimit && textLimit.remain === 0) {
      return acc
    }

    if (typeof token === 'string') {
      if (token === '') {
        return acc
      }

      if (textLimit) {
        if (token.length <= textLimit.remain) {

          textLimit.remain -= token.length
          return [
            ...acc,
            token,
          ]
        }

        const spaceIndex = token.lastIndexOf(' ', textLimit.remain)

        // this token is longer than the maximum length, we won’t need the rest of the tokens
        textLimit.remain = 0

        if (spaceIndex === -1) {
          // that means the current word is too long, let's cut it off entirely
          return acc
        }

        const nextToken = token.substr(0, spaceIndex)
        if (nextToken.length) {
          return [
            ...acc,
            nextToken,
          ]
        }

        return acc
      }

      return [
        ...acc,
        token,
      ]
    }

    if (token.type === 'video' || token.type === 'image') {
      return acc
    }

    if (token.text && token.text.length) {
      const nextText = cleanUpTokens(token.text, textLimit)
      if (nextText.length) {
        return [
          ...acc,
          {
            ...token,
            text: nextText,
          },
        ]
      }

      return acc
    }


    return [
      ...acc,
      token,
    ]
  }, [])

  return result
}

type MarkdownTextProps = {
  text: string
  maxLength?: number | null
  onReadMore?: () => void
}

export const MarkdownText: FC<MarkdownTextProps> = (props) => {
  const {
    text,
    maxLength = null,
    onReadMore,
  } = props

  const renderTokensToJsx = useMemo(() => {
    return createRenderTokensToJsx({})
  }, [])

  const {
    tokens,
    limit,
  } = useMemo(() => {
    const rawTokens = parseMarkdown(text)

    const textLimit = maxLength !== null ? { remain: maxLength } : null
    let resultTokens = cleanUpTokens(rawTokens, textLimit)

    const firstChild = resultTokens[0]
    if (resultTokens.length === 1 && typeof firstChild !== 'string' && firstChild.type === 'paragraph') {
      resultTokens = firstChild.text
    }

    return {
      tokens: resultTokens,
      limit: textLimit,
    }
  }, [text, maxLength])

  const isCutted = useMemo(() => {
    return limit && limit.remain === 0
  }, [limit])

  const contentChild = useMemo(() => {
    return renderTokensToJsx(tokens)
  }, [tokens, renderTokensToJsx])

  return (
    <>
      { contentChild }
      { isCutted && (
        <span className={ styles.readMore } onClick={ onReadMore }>
          ... <span className={ styles.readMoreText }>Read more</span>
        </span>
      )}
    </>
  )
}
